import { useState, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { RenoTicket, RenoTicketStatus } from '../types/renoTicket';
import { toast } from 'react-toastify';
import {
  buildRenoTicketParams,
  RenoTicketFilters as Filters,
} from '../utils/renoTicketParams';

export type { RenoTicketFilters } from '../utils/renoTicketParams';

export function useRenoTickets() {
  const { session } = useSession();
  const [tickets, setTickets] = useState<RenoTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [completionCount, setCompletionCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedStatuses, setSelectedStatuses] = useState<RenoTicketStatus[]>([]);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const fetchTickets = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const params = buildRenoTicketParams(filters, {
        statuses: selectedStatuses,
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets?${params}`,
        { method: 'GET' },
        session,
      );

      if (!res.ok) throw new Error('Ophalen van reno-tickets mislukt');
      const data = await res.json();
      setTickets(data.tickets);
      setTotal(data.total);
      setOpenCount(data.openCount ?? 0);
      setCompletionCount(data.completionCount ?? 0);
    } catch (err: any) {
      toast.error(err.message || 'Fout bij ophalen van reno-tickets');
    } finally {
      setIsLoading(false);
    }
  }, [session, page, pageSize, filters, selectedStatuses, sortBy, sortOrder]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const toggleSort = useCallback((column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'));
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
    setPage(1);
  }, [sortBy]);

  const updateTicket = useCallback(
    async (id: number, updates: Partial<RenoTicket>) => {
      if (!session) return;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets/${id}`,
          { method: 'PATCH', body: JSON.stringify(updates), headers: { 'Content-Type': 'application/json' } },
          session,
        );
        if (!res.ok) throw new Error('Update mislukt');
        await fetchTickets();
        toast.success('Ticket bijgewerkt');
      } catch (err: any) {
        toast.error(err.message || 'Fout bij bijwerken van ticket');
      }
    },
    [session, fetchTickets],
  );

  const createTicket = useCallback(
    async (data: Partial<RenoTicket>) => {
      if (!session) return null;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets`,
          { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } },
          session,
        );
        if (!res.ok) throw new Error('Aanmaken mislukt');
        const created = await res.json();
        await fetchTickets();
        toast.success(`Ticket RT-${created.id} aangemaakt`);
        return created;
      } catch (err: any) {
        toast.error(err.message || 'Fout bij aanmaken van ticket');
        return null;
      }
    },
    [session, fetchTickets],
  );

  const deleteTicket = useCallback(
    async (id: number) => {
      if (!session) return;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets/${id}`,
          { method: 'DELETE' },
          session,
        );
        if (!res.ok) throw new Error('Verwijderen mislukt');
        await fetchTickets();
        toast.success('Ticket verwijderd');
      } catch (err: any) {
        toast.error(err.message || 'Fout bij verwijderen van ticket');
      }
    },
    [session, fetchTickets],
  );

  return {
    tickets,
    total,
    openCount,
    completionCount,
    page,
    setPage,
    pageSize,
    isLoading,
    filters,
    setFilters,
    selectedStatuses,
    setSelectedStatuses,
    sortBy,
    sortOrder,
    toggleSort,
    fetchTickets,
    updateTicket,
    createTicket,
    deleteTicket,
  };
}
